import { Button, Drawer, Form, Input, message, Switch } from 'antd';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  FETCHING_STATUS,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import React, { useEffect } from 'react';
import { BCCService } from 'services/BCCService';
import { ContractorsService } from 'services/ContractorsService';
import { isValidIin } from 'utils/forms/iinValidation';
import {
  iinRule,
  onlyNumberRule,
  requiredRule,
} from 'utils/forms/validationRules';

function ContractorsForm({
  isActive,
  onClose,
  onSuccess,
  initialValues,
  clientId,
}) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;

  useEffect(() => {
    if (initialValues) {
      const { nameRu, nameKz, addressRu, addressKz, bin_iin, is_active, kogd } =
        initialValues;

      form.setFieldsValue({
        nameRu,
        nameKz,
        addressRu,
        addressKz,
        bin_iin,
        is_active,
        kogd,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const handleSubmit = (values) => {
    if (isEditing) {
      handleUpdate(
        { ...values, sender_id: initialValues?.sender_id },
        initialValues.id
      );
    } else {
      handleCreate({ ...values, sender_id: clientId });
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await ContractorsService.createContractor(data);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await ContractorsService.updateContractor(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onClose();
    onSuccess();
    form.resetFields();
  };

  const handleIINChange = async (event) => {
    const iin = event.target.value;
    const canFetchInfo = isValidIin(iin);

    if (canFetchInfo) {
      const hide = message.loading(FETCHING_STATUS);

      try {
        const { full_name, kogd } = await BCCService.getPersonInfoByIIN(iin);

        form.setFieldsValue({
          nameRu: full_name,
          kogd,
        });

        hide();
      } catch (err) {
        message.error('Не удалось получить данные контрагента');
        hide();
      }
    }
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование контрагента' : 'Создание контрагента'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
      >
        <Form.Item
          name="bin_iin"
          label="БИН/ИИН"
          rules={[requiredRule, onlyNumberRule, iinRule]}
        >
          <Input
            allowClear
            onChange={handleIINChange}
            maxLength={12}
            placeholder="Укажите БИН/ИИН"
          />
        </Form.Item>

        <Form.Item name="kogd" label="КОГД">
          <Input placeholder="КОГД" allowClear />
        </Form.Item>

        <Form.Item
          name="nameRu"
          label="Наименование (ru)"
          rules={[requiredRule]}
        >
          <Input placeholder="Наименование на русском" allowClear />
        </Form.Item>

        <Form.Item
          name="nameKz"
          label="Наименование (kz)"
          rules={[requiredRule]}
        >
          <Input placeholder="Наименование на казахском" allowClear />
        </Form.Item>

        <Form.Item name="addressRu" label="Адрес (ru)" rules={[requiredRule]}>
          <Input placeholder="Адрес на русском" allowClear />
        </Form.Item>

        <Form.Item name="addressKz" label="Адрес (kz)" rules={[requiredRule]}>
          <Input placeholder="Адрес на казахском" allowClear />
        </Form.Item>

        {isEditing && (
          <Form.Item name="is_active" label="Активный" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { ContractorsForm };
